import React from 'react'
import { Stack, useTheme } from '@mui/system'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { enUS, svSE } from '@mui/x-data-grid-pro/locales'
import { useTranslation } from 'react-i18next'
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material'
import { Link } from 'react-router-dom'
import InvestorApi from '../../../api/InvestorApi'
import useConfirmationDialog from '../../../components/dialogs/ConfirmationDialog'
import { CustomDataGridToolbar } from '../../../components/CustomDataGridToolbar'

const gridTextI18n = (locale) => {
    if (locale?.startsWith('sv')) {
        return svSE.components.MuiDataGrid.defaultProps.localeText
    } else {
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }
}

const disableSelectedSellOutlineStyle = {
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: 'none',
    },
    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
        outline: 'none',
    },
}

function shouldShowAddButton(investorAlias) {
    return !investorAlias.isPrimary || (investorAlias.isPrimary && investorAlias.aliasCount === 1)
}

function shouldShowRemoveButton(investorAlias) {
    return !investorAlias.isPrimary
}

function shouldShowDeleteButton(investorAlias) {
    return investorAlias.isPrimary && investorAlias.aliasCount <= 1 && investorAlias.investorOccurrences <= 0
}

function shouldShowSetPrimaryButton(investorAlias) {
    return !investorAlias.isPrimary
}

function SelectInvestor({ investors, value, onChange }) {
    const [inputValue, setInputValue] = React.useState('')
    return (
        <Autocomplete
            options={investors}
            value={value}
            getOptionLabel={(option) => option.name || 'MISSING'} // Set label for each option
            isOptionEqualToValue={(option, value) => option.id === value.id} // Match options by ID
            onChange={(event, newValue) => {
                onChange(newValue)
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue)
            }}
            style={{ width: 300 }}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    {' '}
                    {/* Explicitly set key */}
                    {option.name}
                </li>
            )}
            renderInput={(params) => <TextField {...params} label='Investerare' variant='outlined' />}
        />
    )
}

function ConnectInvestorAliasDialog({ open, onClose, investorAlias, investors, onConnect }) {
    const [selectedInvestor, setSelectedInvestor] = React.useState(null)
    if (!investorAlias || !investors) {
        return null
    }
    const handleConnect = () => {
        onConnect(investorAlias, selectedInvestor)
    }
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Koppla alias till investerare</DialogTitle>
            <DialogContent>
                <Stack direction='column' spacing={2}>
                    <Typography variant='body1'>Alias: {investorAlias.name}</Typography>
                    <SelectInvestor investors={investors} value={selectedInvestor} onChange={setSelectedInvestor} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' color='primary' onClick={handleConnect}>
                    Koppla
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function InvestorsGrid({ investors, setInvestors }) {
    const { confirm, ConfirmationDialog } = useConfirmationDialog()
    const [investorsRows, setInvestorsRows] = React.useState(null)
    const [connectInvestorAliasDialogOpen, setConnectInvestorAliasDialogOpen] = React.useState(false)
    const [selectedInvestorAlias, setSelectedInvestorAlias] = React.useState(null)
    const handleInvestorAliasDialogClose = () => setConnectInvestorAliasDialogOpen(false)
    const [filterModel, setFilterModel] = React.useState({
        items: [],
        quickFilterExcludeHiddenColumns: true,
    })
    const handleFilterModelChange = React.useCallback((newModel) => setFilterModel(newModel), [])
    const { i18n } = useTranslation()
    const locale = i18n.language
    const theme = useTheme()
    React.useEffect(() => {
        if (!investors) return
        const aliasList = []
        for (const investor of investors) {
            for (const alias of investor.aliases || []) {
                aliasList.push({
                    ...alias,
                    isPrimary: investor.name === alias.name,
                    investorId: investor.id,
                    investorName: investor.name,
                    id: alias.aliasId,
                    investorOccurrences: investor.occurrences,
                    aliasCount: investor.aliases.length,
                })
            }
        }
        // sort by investorOccurrence (desc), investorName (asc), isPrimary (desc), occurrence (desc)
        aliasList.sort((a, b) => {
            if (a.investorOccurrences !== b.investorOccurrences) {
                return b.investorOccurrences - a.investorOccurrences
            }
            if (a.investorName !== b.investorName) {
                return a.investorName.localeCompare(b.investorName)
            }
            if (a.isPrimary !== b.isPrimary) {
                return b.isPrimary - a.isPrimary
            }
            return b.occurrences - a.occurrences
        })
        setInvestorsRows(aliasList)
    }, [investors])

    const [paginationModel, setPaginationModel] = React.useState({ pageSize: 25, page: 0 })
    const openAddAliasDialog = (investorAlias) => {
        setSelectedInvestorAlias(investorAlias)
        setConnectInvestorAliasDialogOpen(true)
    }

    const handleConnectAlias = async (investorAlias, investor) => {
        console.log('investorAlias to connect', investorAlias)
        if (investorAlias.investorId === investor.id) {
            console.log('Alias is already connected to this investor')
            setConnectInvestorAliasDialogOpen(false)
            return
        }
        const api = new InvestorApi()
        await api.connectAsInvestorAlias(investorAlias.id, investor.id)
        setConnectInvestorAliasDialogOpen(false)
        console.log(`Connected alias ${investorAlias.name} to investor ${investor.name}`)

        const oldInvestor = investors.find((i) => i.id === investorAlias.investorId)
        // if oldInvestor has only one alias, we can remove it
        if (oldInvestor.aliases.length === 1) {
            const index = investors.findIndex((i) => i.id === oldInvestor.id)
            investors.splice(index, 1)
        } else {
            // remove the alias from the old investor
            oldInvestor.aliases = oldInvestor.aliases.filter((a) => a.aliasId !== investorAlias.id)
        }
        const newInvestor = investors.find((i) => i.id === investor.id)
        // add the alias to the new investor
        newInvestor.aliases.push(investorAlias)
        // update the investors list
        setInvestors([...investors])
    }

    const handleAddAlias = (aliasObj) => {
        openAddAliasDialog(aliasObj)
    }

    const handleRemoveAlias = async (aliasObj) => {
        const confirmed = await confirm(
            'Är du säker på att du vill ta bort aliaset och göra om det till en investerare?',
        )
        if (!confirmed) return
        const api = new InvestorApi()
        const { id: newInvestorId } = await api.setAliasAsToplevelInvestor(aliasObj.id, aliasObj.investorId)
        console.log(`Promoted alias ${aliasObj.name} to investor ${newInvestorId}`)

        const oldInvestor = investors.find((i) => i.id === aliasObj.investorId)
        // if oldInvestor has only one alias, we can remove it
        if (oldInvestor.aliases.length === 1) {
            const index = investors.findIndex((i) => i.id === oldInvestor.id)
            investors.splice(index, 1)
        } else {
            // remove the alias from the old investor
            oldInvestor.aliases = oldInvestor.aliases.filter((a) => a.aliasId !== aliasObj.id)
        }
        // add the alias as a new investor
        const newInvestor = { id: newInvestorId, name: aliasObj.name, aliases: [aliasObj] }
        investors.push(newInvestor)
        // update the investors list
        setInvestors([...investors])
    }

    const handleDeleteInvestor = async (aliasObj) => {
        if (!aliasObj?.investorId) {
            console.error('No investorId to delete')
            return
        }
        const api = new InvestorApi()
        await api.deleteInvestorAndAliases(aliasObj.investorId)
        const newInvestors = investors.filter((i) => i.id !== aliasObj.investorId)
        setInvestors([...newInvestors])
    }

    const handleSetPrimary = async (aliasObj) => {
        const confirmed = await confirm(
            `Är du säker på att du vill ange "${aliasObj.name}" som investerarens primärnamn?`,
        )
        if (!confirmed) return
        const api = new InvestorApi()
        await api.setInvestorPrimaryNameFromAlias(aliasObj.investorId, aliasObj.id)
        console.log(`Set ${aliasObj.name} as primary name for investor ${aliasObj.investorId}`)
        // update the investor list
        const investor = investors.find((i) => i.id === aliasObj.investorId)
        investor.name = aliasObj.name
        investor.aliases.forEach((a) => {
            a.isPrimary = a.id === aliasObj.id
        })
        setInvestors([...investors])
    }

    if (!investorsRows) {
        return null
    }

    const columnDefinitions = [
        {
            field: 'investorName',
            headerName: 'Namn',
            flex: 1,
            renderCell: (params) => {
                if (params.row.isPrimary) {
                    return <Link to={`/investors/${params.row.investorId}`}>{params.value}</Link>
                } else {
                    return <i>* {params.row.name}</i>
                }
            },
        },
        { field: 'occurrences', headerName: 'Förekomster', flex: 1, getApplyQuickFilterFn: () => null },
        {
            field: 'actions',
            headerName: 'Åtgärder',
            sortable: false,
            renderCell: (params) => (
                <Stack direction='row' spacing={1}>
                    {shouldShowAddButton(params.row) && (
                        <Button
                            variant='contained'
                            size='small'
                            color='primary'
                            onClick={() => handleAddAlias(params.row)}
                        >
                            Lägg till
                        </Button>
                    )}

                    {shouldShowDeleteButton(params.row) && (
                        <Button
                            variant='contained'
                            size='small'
                            color='warning'
                            onClick={() => handleDeleteInvestor(params.row)}
                        >
                            Radera
                        </Button>
                    )}

                    {shouldShowRemoveButton(params.row) && (
                        <Button
                            variant='contained'
                            size='small'
                            color='secondary'
                            onClick={() => handleRemoveAlias(params.row)}
                        >
                            Ta bort
                        </Button>
                    )}

                    {shouldShowSetPrimaryButton(params.row) && (
                        <Button
                            variant='contained'
                            size='small'
                            color='secondary'
                            onClick={() => handleSetPrimary(params.row)}
                        >
                            Ange primär
                        </Button>
                    )}
                </Stack>
            ),
            flex: 1,
        },
    ]

    return (
        <>
            <ConfirmationDialog />
            <ConnectInvestorAliasDialog
                open={connectInvestorAliasDialogOpen}
                onClose={handleInvestorAliasDialogClose}
                investorAlias={selectedInvestorAlias}
                investors={investors}
                onConnect={handleConnectAlias}
            />
            <DataGridPro
                rows={investorsRows}
                columns={columnDefinitions}
                slots={{ toolbar: CustomDataGridToolbar }}
                filterModel={filterModel}
                onFilterModelChange={handleFilterModelChange}
                density='compact'
                pagination={true}
                pageSizeOptions={[10, 25, 100, 500]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sx={{
                    ...disableSelectedSellOutlineStyle,
                    width: '100%',
                    '& .even': {
                        backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                    },
                    '& .odd': {
                        // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                    },
                }}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                localeText={gridTextI18n(locale)}
                showToolbar
            />
        </>
    )
}

export default InvestorsGrid
